import { ALL_AI, type BridgeSeat as Seat } from '../bridge/seats.js';
import { seededRandom } from '../trick/rng.js';
import {
  type MahjongGame,
  type NewMahjongOptions,
  mahjongClaimChow,
  mahjongClaimKong,
  mahjongClaimPung,
  mahjongClaimWin,
  mahjongDiscard,
  mahjongKong,
  mahjongPass,
  mahjongSelfWin,
  newMahjongGame,
} from './play.js';
import { type MahjongAction, type MahjongRobotLevel, mahjongChoose } from './robot.js';
import { mahjongViewFor } from './view.js';

export type MahjongLevels = MahjongRobotLevel | Readonly<Partial<Record<Seat, MahjongRobotLevel>>>;
const levelOf = (levels: MahjongLevels, seat: Seat): MahjongRobotLevel => (typeof levels === 'string' ? levels : levels[seat] ?? 'easy');

/** Apply an action chosen by a robot (or a hint) to the game, whichever phase it is in. */
export function applyMahjongAction(game: MahjongGame, action: MahjongAction): MahjongGame {
  if (game.phase === 'claim') {
    switch (action.type) {
      case 'win':
        return mahjongClaimWin(game);
      case 'pung':
        return mahjongClaimPung(game);
      case 'kong':
        return mahjongClaimKong(game);
      case 'chow':
        return mahjongClaimChow(game, action.tiles);
      case 'pass':
        return mahjongPass(game);
      default:
        throw new Error(`a ${action.type} is not a claim`);
    }
  }
  switch (action.type) {
    case 'win':
      return mahjongSelfWin(game);
    case 'kong':
      return mahjongKong(game, action.tile as string);
    case 'discard':
      return mahjongDiscard(game, action.tile);
    default:
      throw new Error(`a ${action.type} is not an action on your own turn`);
  }
}

/** One robot action applied to a game; each robot reads only its own view. */
export function mahjongStep(game: MahjongGame, levels: MahjongLevels, random: () => number): MahjongGame {
  const seat = game.turn;
  return applyMahjongAction(game, mahjongChoose(mahjongViewFor(game, seat), levelOf(levels, seat), random));
}

/** Play a whole hand with robots on every seat. `maxSteps` is a safety net: a hand always ends well before it. */
export function playMahjongHand(seed: number, levels: MahjongLevels, opts: Partial<NewMahjongOptions> = {}, maxSteps = 2000): MahjongGame {
  let game = newMahjongGame({ ...opts, seed, controllers: ALL_AI });
  const random = seededRandom(seed ^ 0x3a7c91);
  for (let i = 0; game.phase !== 'done'; i++) {
    if (i > maxSteps) throw new Error(`mahjong hand ${seed} did not end in ${maxSteps} steps`);
    game = mahjongStep(game, levels, random);
  }
  return game;
}
